import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { markCreatorTourPending, replayCreatorTour } from "./CreatorWelcomeTour";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function useCreatorMode() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const setCreator = async (enabled) => {
    setBusy(true);
    try {
      await api.put("/users/me/creator", { enabled });
      await refresh();
      toast.success(enabled ? "Creator mode is on. Go Live and Creator tools are yours." : "Creator mode turned off.");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update creator mode.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { isCreator: !!user?.is_creator, busy, setCreator };
}

export function BecomeCreatorButton({ className = "", variant = "nav", onDone }) {
  const { busy, setCreator } = useCreatorMode();
  const navigate = useNavigate();
  const go = async () => {
    if (await setCreator(true)) {
      markCreatorTourPending();
      onDone?.();
      navigate("/creator");
    }
  };
  const trigger = variant === "nav" ? (
    <button
      type="button"
      data-testid="become-creator-btn"
      className={`flex items-center gap-4 px-4 py-3 w-full text-left border-l-2 border-transparent text-amber-400 hover:text-white hover:bg-white/[0.02] transition-colors duration-200 ${className}`}
    >
      <Sparkles size={20} strokeWidth={1.6} />
      <span className="text-sm tracking-wide">Become a Creator</span>
    </button>
  ) : (
    <Button data-testid="become-creator-btn" className={`bg-rose-600 hover:bg-rose-500 text-white rounded-none ${className}`}>
      <Sparkles size={14} className="mr-2" /> Become a Creator
    </Button>
  );
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent data-testid="become-creator-dialog" className="bg-zinc-950 border-white/10 text-white rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">Turn on Creator mode?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400 text-sm">
            You get Go Live, the Creator dashboard, live tools, prize wheel and videos. Your account, coins, role and payouts stay exactly as they are — and you can turn it off anytime from Creator → Settings.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="become-creator-cancel" className="rounded-none bg-transparent border-white/15 text-zinc-300 hover:text-white">Not now</AlertDialogCancel>
          <AlertDialogAction data-testid="become-creator-confirm" disabled={busy} onClick={go} className="rounded-none bg-rose-600 hover:bg-rose-500 text-white">
            {busy ? "Turning on…" : "Turn on Creator mode"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function CreatorModeSettingsCard() {
  const { user } = useAuth();
  const { busy, setCreator } = useCreatorMode();
  const navigate = useNavigate();
  if (user?.role === "admin" && !user?.is_creator) return null;
  const off = async () => {
    if (await setCreator(false)) navigate("/feed");
  };
  return (
    <div className="border border-white/10 p-5 mt-8" data-testid="creator-mode-card">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Creator mode</div>
      <p className="text-sm text-zinc-400 mb-4">Creator mode is on. Turning it off hides the Creator menu, Go Live and creator tools. Nothing else about your account changes, and you can turn it back on anytime.</p>
      <div className="flex flex-wrap gap-2">
      <Button variant="ghost" data-testid="creator-tour-replay-btn" onClick={() => { replayCreatorTour(user.id); window.dispatchEvent(new Event("creator-tour:replay")); }} className="rounded-none text-rose-300 hover:text-white">Replay welcome tour</Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" data-testid="creator-mode-off-btn" className="rounded-none border-white/15 text-zinc-300 hover:text-white">Turn off Creator mode</Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-zinc-950 border-white/10 text-white rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Turn off Creator mode?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400 text-sm">Your coins, earnings, followers and content stay. Only the creator tools are hidden until you turn it back on.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none bg-transparent border-white/15 text-zinc-300 hover:text-white">Keep it on</AlertDialogCancel>
            <AlertDialogAction data-testid="creator-mode-off-confirm" disabled={busy} onClick={off} className="rounded-none bg-rose-600 hover:bg-rose-500 text-white">Turn off</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
}
